import {
  ArrowTrendingUpIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { toast } from 'react-hot-toast';
import SEOHead from '../../components/SEOHead';
import { useLocale } from '../../contexts/LocaleContext';
import { adminService } from '../../services/adminService';
import { AdminStats } from '../../types/admin';
import { generateCanonicalUrl } from '../../utils/seoUtils';

const Analytics: React.FC = () => {
  const { formatCurrency, convertPrice } = useLocale();
  const { data, isLoading, isError } = useQuery<AdminStats>({
    queryKey: ['admin', 'advanced-stats'],
    queryFn: async () => await adminService.getAdvancedStats(),
  });

  // Handle error with useEffect instead of onError
  React.useEffect(() => {
    if (isError) {
      toast.error('Failed to load analytics');
    }
  }, [isError]);

  if (isLoading) {
    return (
      <div className='space-y-6'>
        <div className='h-8 bg-gray-200 rounded w-48 animate-pulse' />
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
          {[1, 2, 3].map(i => (
            <div key={i} className='h-28 bg-gray-200 rounded animate-pulse' />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return <div className='text-gray-600'>Failed to load analytics.</div>;
  }

  const { revenueStats, orderStats, userStats } = data as AdminStats;
  const formatAmount = (amount: number) => formatCurrency(convertPrice(amount));

  return (
    <div className='space-y-6'>
      <SEOHead
        title='Admin Analytics'
        description='Sales, orders, and user analytics overview.'
        url={generateCanonicalUrl('/admin/analytics')}
        type='website'
      />

      <div className='md:flex md:items-center md:justify-between'>
        <div className='flex-1 min-w-0'>
          <h2 className='text-2xl font-bold text-gray-900'>Analytics</h2>
          <p className='mt-1 text-sm text-gray-500'>Key metrics and trends</p>
        </div>
      </div>

      {/* Revenue */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
        <StatCard
          title='Total Revenue'
          value={formatAmount(revenueStats.totalRevenue)}
          subtitle={`This month: ${formatAmount(revenueStats.monthlyRevenue)}`}
          Icon={BanknotesIcon}
          color='green'
        />
        <StatCard
          title='Monthly Revenue'
          value={formatAmount(revenueStats.monthlyRevenue)}
          subtitle={`This week: ${formatAmount(revenueStats.weeklyRevenue)}`}
          Icon={ArrowTrendingUpIcon}
          color='emerald'
        />
        <StatCard
          title='Weekly Revenue'
          value={formatAmount(revenueStats.weeklyRevenue)}
          subtitle='Rolling 7 days'
          Icon={ArrowTrendingUpIcon}
          color='teal'
        />
      </div>

      {/* Orders */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        <Card className='border'>
          <CardHeader>
            <h3 className='text-lg font-medium text-gray-900'>Orders</h3>
          </CardHeader>
          <CardBody>
            <div className='grid grid-cols-2 sm:grid-cols-3 gap-4'>
              <MiniStat label='Total' value={orderStats.totalOrders} />
              <MiniStat label='Pending' value={orderStats.pendingOrders} />
              <MiniStat
                label='Processing'
                value={orderStats.processingOrders}
              />
              <MiniStat label='Shipped' value={orderStats.shippedOrders} />
              <MiniStat label='Delivered' value={orderStats.deliveredOrders} />
              <MiniStat label='Cancelled' value={orderStats.cancelledOrders} />
              <MiniStat label='This Month' value={orderStats.ordersThisMonth} />
              <MiniStat label='This Week' value={orderStats.ordersThisWeek} />
            </div>
            <div className='mt-6'>
              <Bar
                items={[
                  {
                    label: 'Pending',
                    value: orderStats.pendingOrders,
                    color: 'bg-yellow-400',
                  },
                  {
                    label: 'Processing',
                    value: orderStats.processingOrders,
                    color: 'bg-blue-500',
                  },
                  {
                    label: 'Shipped',
                    value: orderStats.shippedOrders,
                    color: 'bg-indigo-500',
                  },
                  {
                    label: 'Delivered',
                    value: orderStats.deliveredOrders,
                    color: 'bg-green-500',
                  },
                  {
                    label: 'Cancelled',
                    value: orderStats.cancelledOrders,
                    color: 'bg-red-500',
                  },
                ]}
                total={
                  orderStats.pendingOrders +
                  orderStats.processingOrders +
                  orderStats.shippedOrders +
                  orderStats.deliveredOrders +
                  orderStats.cancelledOrders
                }
              />
            </div>
          </CardBody>
        </Card>

        {/* Users */}
        <Card className='border'>
          <CardHeader>
            <h3 className='text-lg font-medium text-gray-900'>Users</h3>
          </CardHeader>
          <CardBody>
            <div className='grid grid-cols-2 sm:grid-cols-3 gap-4'>
              <MiniStat label='Total' value={userStats.totalUsers} />
              <MiniStat label='Active' value={userStats.activeUsers} />
              <MiniStat
                label='New (Month)'
                value={userStats.newUsersThisMonth}
              />
              <MiniStat label='New (Week)' value={userStats.newUsersThisWeek} />
            </div>
            <div className='mt-6'>
              <Bar
                items={[
                  {
                    label: 'Active',
                    value: userStats.activeUsers,
                    color: 'bg-blue-500',
                  },
                  {
                    label: 'New M',
                    value: userStats.newUsersThisMonth,
                    color: 'bg-green-500',
                  },
                  {
                    label: 'New W',
                    value: userStats.newUsersThisWeek,
                    color: 'bg-emerald-500',
                  },
                ]}
                total={Math.max(
                  userStats.totalUsers,
                  userStats.activeUsers + userStats.newUsersThisMonth
                )}
              />
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

const StatCard: React.FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  Icon: React.ComponentType<any>;
  color: string;
}> = ({ title, value, subtitle, Icon, color }) => (
  <Card className='border'>
    <CardBody className='p-6'>
      <div className='flex items-center'>
        <div
          className={`h-12 w-12 rounded-lg bg-${color}-100 flex items-center justify-center`}
        >
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
        <div className='ml-4'>
          <div className='text-2xl font-bold text-gray-900'>{value}</div>
          <div className='text-sm text-gray-600'>{title}</div>
          {subtitle && <div className='text-xs text-gray-500'>{subtitle}</div>}
        </div>
      </div>
    </CardBody>
  </Card>
);

const MiniStat: React.FC<{ label: string; value: number }> = ({
  label,
  value,
}) => (
  <div className='p-4 rounded-lg border bg-white'>
    <div className='text-2xl font-bold text-gray-900'>{value}</div>
    <div className='text-xs text-gray-500'>{label}</div>
  </div>
);

const Bar: React.FC<{
  items: { label: string; value: number; color: string }[];
  total: number;
}> = ({ items, total }) => {
  const sum = items.reduce((a, b) => a + b.value, 0);
  const denom = total > 0 ? total : sum || 1;
  return (
    <div>
      <div className='flex h-3 w-full overflow-hidden rounded bg-gray-100 border'>
        {items.map((i, idx) => {
          const pct = Math.max(
            0,
            Math.min(100, Math.round((i.value / denom) * 100))
          );
          return (
            <div
              key={idx}
              className={`${i.color}`}
              style={{ width: `${pct}%` }}
            />
          );
        })}
      </div>
      <div className='mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-gray-600'>
        {items.map((i, idx) => (
          <div key={idx} className='flex items-center gap-2'>
            <span className={`inline-block w-3 h-3 rounded ${i.color}`} />
            <span>
              {i.label}: {i.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Analytics;
